import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Center, Group, Loader, MultiSelect, Pagination, SimpleGrid, Stack, Switch, Text, TextInput, Title } from '@mantine/core';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

interface Job {
  id: number;
  title: string;
  company_name: string;
  location: string;
  job_type: string;
  is_remote: boolean;
  is_approved?: boolean;
  is_active?: boolean;
  experience_range?: string;
  requires_at_title?: boolean;
  requires_at_diploma_uba?: boolean;
  requires_graduated?: boolean;
  requires_school_inclusion_course?: boolean;
  requires_hr_courses?: boolean;
  requirements?: string;
  careers_affine?: string[];
  short_description?: string;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [appliedJobIds, setAppliedJobIds] = useState<number[]>([]);
  const [careerFilter, setCareerFilter] = useState<string[]>([]);
  const [positionFilter, setPositionFilter] = useState<string[]>([]);
  const [positionOther, setPositionOther] = useState('');
  const [positionOtherDebounced, setPositionOtherDebounced] = useState('');
  const [requiresGraduated, setRequiresGraduated] = useState(false);
  const [requiresAtTitle, setRequiresAtTitle] = useState(false);
  const [requiresAtDiplomaUba, setRequiresAtDiplomaUba] = useState(false);
  const [requiresSchoolInclusionCourse, setRequiresSchoolInclusionCourse] = useState(false);
  const [requiresHrCourses, setRequiresHrCourses] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const jobsPerPage = 9;

  useEffect(() => {
    const handler = window.setTimeout(() => {
      loadJobs();
    }, 350);

    return () => window.clearTimeout(handler);
  }, [
    user,
    careerFilter,
    positionFilter,
    positionOtherDebounced,
    requiresGraduated,
    requiresAtTitle,
    requiresAtDiplomaUba,
    requiresSchoolInclusionCourse,
    requiresHrCourses,
    activeOnly,
    page,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    careerFilter,
    positionFilter,
    positionOtherDebounced,
    requiresGraduated,
    requiresAtTitle,
    requiresAtDiplomaUba,
    requiresSchoolInclusionCourse,
    requiresHrCourses,
    activeOnly,
  ]);

  useEffect(() => {
    if (user?.user_type === 'JOB_SEEKER') {
      loadAppliedJobs();
    }
  }, [user]);

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setPositionOtherDebounced(positionOther.trim());
    }, 350);

    return () => window.clearTimeout(handler);
  }, [positionOther]);

  const careerOptions = [
    { value: 'MUSICOTERAPIA', label: t('careers.musicotherapy') },
    { value: 'PSICOLOGIA', label: t('careers.psychology') },
    { value: 'PROFESORADO_PSICOLOGIA', label: t('careers.psychologyTeaching') },
    { value: 'TERAPIA_OCUPACIONAL', label: t('careers.occupationalTherapy') },
  ];

  const careerLabelMap: Record<string, string> = {
    MUSICOTERAPIA: t('careers.musicotherapy'),
    PSICOLOGIA: t('careers.psychology'),
    PROFESORADO_PSICOLOGIA: t('careers.psychologyTeaching'),
    TERAPIA_OCUPACIONAL: t('careers.occupationalTherapy'),
  };

  const buildCareerFilterParams = () => {
    const expanded = new Set<string>();
    careerFilter.forEach((career) => {
      expanded.add(career);
      const label = careerLabelMap[career];
      if (label) expanded.add(label);
    });
    return Array.from(expanded);
  };

  const jobTypeLabelMap: Record<string, string> = {
    FULL_TIME: 'Full Time',
    PART_TIME: 'Part Time',
    CONTRACT: 'Contract',
    INTERNSHIP: 'Internship',
    TEMPORARY: 'Temporary',
  };

  const positionOtherValue = 'OTHER';
  const positionOptions = [
    { value: t('jobCategories.therapeuticAssistant'), label: t('jobCategories.therapeuticAssistant') },
    { value: t('jobCategories.schoolAssistant'), label: t('jobCategories.schoolAssistant') },
    { value: t('jobCategories.hr'), label: t('jobCategories.hr') },
    { value: t('jobCategories.psychologyProfessional'), label: t('jobCategories.psychologyProfessional') },
    { value: t('jobCategories.docenteTutor'), label: t('jobCategories.docenteTutor') },
    { value: t('jobCategories.musicoterapeuta'), label: t('jobCategories.musicoterapeuta') },
    { value: t('jobCategories.terapistaOcupacional'), label: t('jobCategories.terapistaOcupacional') },
    { value: t('jobCategories.selectorPersonal'), label: t('jobCategories.selectorPersonal') },
    { value: t('jobCategories.personalAdministrativo'), label: t('jobCategories.personalAdministrativo') },
    { value: t('jobCategories.orientadorSala'), label: t('jobCategories.orientadorSala') },
    { value: t('jobCategories.acompananteGerontologico'), label: t('jobCategories.acompananteGerontologico') },
    { value: t('jobCategories.coordinadorIntegraciones'), label: t('jobCategories.coordinadorIntegraciones') },
    { value: t('jobCategories.supervisorAts'), label: t('jobCategories.supervisorAts') },
    { value: positionOtherValue, label: t('jobCategories.other') },
  ];

  const loadAppliedJobs = async () => {
    try {
      const response = await api.getMyApplications();
      const appData = response;
      const jobIds = appData.map((app: any) => app.job);
      setAppliedJobIds(jobIds);
    } catch (error) {
      console.error('Failed to load applied jobs:', error);
    }
  };

  const loadJobs = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {
        page: String(page),
        page_size: String(jobsPerPage),
      };
      if (careerFilter.length > 0) params.careers = buildCareerFilterParams().join(',');
      if (positionFilter.length > 0) {
        const selectedTitles = positionFilter.filter((item) => item !== positionOtherValue);
        if (selectedTitles.length > 0) params.titles = selectedTitles.join(',');
      }
      if (positionFilter.includes(positionOtherValue) && positionOtherDebounced) {
        params.title_contains = positionOtherDebounced;
      }
      if (requiresGraduated) params.requires_graduated = 'true';
      if (requiresAtTitle) params.requires_at_title = 'true';
      if (requiresAtDiplomaUba) params.requires_at_diploma_uba = 'true';
      if (requiresSchoolInclusionCourse) params.requires_school_inclusion_course = 'true';
      if (requiresHrCourses) params.requires_hr_courses = 'true';
      if (activeOnly) params.is_active = 'true';

      const response = await api.getJobs(params);
      // Handle paginated response
      const jobsData = Array.isArray(response) ? response : response.results || [];
      const count = Array.isArray(response) ? jobsData.length : response.count || jobsData.length;
      const calculatedPages = Math.max(1, Math.ceil(count / jobsPerPage));
      setJobs(jobsData);
      setTotalPages(calculatedPages);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const showPendingApproval = user?.user_type === 'ADMIN' || user?.user_type === 'MODERATOR';

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{t('dashboard.title')}</Title>
          <Text c="dimmed">{t('dashboard.subtitle')}</Text>
        </div>
        {user?.user_type === 'JOB_POSTER' && (
          <Button component={Link} to="/jobs/create" radius="md">
            {t('nav.postJob')}
          </Button>
        )}
      </Group>

      <Card withBorder padding="md" radius="lg" shadow="sm">
        <Stack gap="sm">
          <Title order={4}>{t('dashboard.filters')}</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <MultiSelect
              label={t('dashboard.careers')}
              data={careerOptions}
              value={careerFilter}
              onChange={setCareerFilter}
              placeholder={t('dashboard.allCareers')}
              clearable
            />
            <MultiSelect
              label={t('dashboard.positions')}
              data={positionOptions}
              value={positionFilter}
              onChange={(value) => {
                setPositionFilter(value);
                if (!value.includes(positionOtherValue)) {
                  setPositionOther('');
                }
              }}
              placeholder={t('dashboard.allPositions')}
              clearable
            />
            {positionFilter.includes(positionOtherValue) && (
              <TextInput
                label={t('dashboard.positionOtherLabel')}
                value={positionOther}
                onChange={(event) => setPositionOther(event.currentTarget.value)}
                placeholder={t('dashboard.positionOtherPlaceholder')}
              />
            )}
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <Switch
              label={t('dashboard.requiresAtCourse')}
              checked={requiresAtTitle}
              onChange={(event) => setRequiresAtTitle(event.currentTarget.checked)}
            />
            <Switch
              label={t('dashboard.requiresAtDiplomaUba')}
              checked={requiresAtDiplomaUba}
              onChange={(event) => setRequiresAtDiplomaUba(event.currentTarget.checked)}
            />
            <Switch
              label={t('dashboard.requiresDegreeTitle')}
              checked={requiresGraduated}
              onChange={(event) => setRequiresGraduated(event.currentTarget.checked)}
            />
            <Switch
              label={t('dashboard.requiresSchoolInclusionCourse')}
              checked={requiresSchoolInclusionCourse}
              onChange={(event) => setRequiresSchoolInclusionCourse(event.currentTarget.checked)}
            />
            <Switch
              label={t('dashboard.requiresHrCourses')}
              checked={requiresHrCourses}
              onChange={(event) => setRequiresHrCourses(event.currentTarget.checked)}
            />
            <Switch
              label={t('dashboard.activeOnly')}
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.currentTarget.checked)}
            />
          </SimpleGrid>
        </Stack>
      </Card>

      {jobs.length === 0 ? (
        <Card withBorder padding="xl" radius="lg" shadow="sm">
          <Text ta="center" c="dimmed">
            {t('dashboard.noJobs')}
          </Text>
        </Card>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {jobs.map((job) => (
            <Card
              key={job.id}
              component={Link}
              to={`/jobs/${job.id}`}
              shadow="sm"
              padding="lg"
              radius="lg"
              withBorder
            >
              {job.careers_affine && job.careers_affine.length > 0 && (
                <Group gap="xs" wrap="wrap" mb={6}>
                  {job.careers_affine.map((career) => (
                    <Badge key={career} variant="outline" color="orange" radius="xl">
                      {careerLabelMap[career] || career}
                    </Badge>
                  ))}
                </Group>
              )}
              <Title order={3}>{job.title}</Title>
              <Text size="sm">
                <Text span c="dimmed">{job.company_name}</Text>
                <Text span c="gray.7"> - {job.location}</Text>
              </Text>
              <Group gap="xs" wrap="wrap" mt={4}>
                <Badge color="gray" variant="light">
                  {jobTypeLabelMap[job.job_type] || job.job_type.replace('_', ' ')}
                </Badge>
                {job.is_remote && (
                  <Badge color="gray" variant="outline">
                    {t('common.remote')}
                  </Badge>
                )}
              </Group>
              {(job.requires_at_title
                || job.requires_at_diploma_uba
                || job.requires_graduated
                || job.requires_school_inclusion_course
                || job.requires_hr_courses
                || job.requirements
                || appliedJobIds.includes(job.id)
                || job.is_active === false
                || (showPendingApproval && job.is_approved === false)) && (
                  <Group gap="xs" wrap="wrap" mt={6}>
                    {job.requires_at_title && (
                      <Badge color="orange" variant="light">{t('jobDetail.requiresAtCourse')}</Badge>
                    )}
                    {job.requires_at_diploma_uba && (
                      <Badge color="orange" variant="light">{t('jobDetail.requiresAtDiplomaUba')}</Badge>
                    )}
                    {job.requires_graduated && (
                      <Badge color="orange" variant="light">{t('jobDetail.requiresDegreeTitle')}</Badge>
                    )}
                    {job.requires_school_inclusion_course && (
                      <Badge color="orange" variant="light">{t('jobDetail.requiresSchoolInclusionCourse')}</Badge>
                    )}
                    {job.requires_hr_courses && (
                      <Badge color="orange" variant="light">{t('jobDetail.requiresHrCourses')}</Badge>
                    )}
                    {job.requirements && (
                      <Text c="dimmed" size="sm">
                        {t('jobDetail.otherSkills')} {job.requirements}
                      </Text>
                    )}
                    {appliedJobIds.includes(job.id) && <Badge color="green" variant="light">Applied</Badge>}
                    {job.is_active === false && (
                      <Badge color="gray" variant="light">{t('postedJobs.inactive')}</Badge>
                    )}
                    {showPendingApproval && job.is_approved === false && (
                      <Badge color="red" variant="light">{t('approvals.pendingApproval')}</Badge>
                    )}
                  </Group>
                )}
              {job.short_description && (
                <Text c="dimmed" size="sm" mt={6} lineClamp={2}>
                  {job.short_description}
                </Text>
              )}
            </Card>
          ))}
        </SimpleGrid>
      )}
      {totalPages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}
    </Stack>
  );
};

export default DashboardPage;
